import { useEffect, useState } from "react";
import { X, Clapperboard } from "lucide-react";
import { fetchPostVideos } from "@/lib/api";

// Regex for a video block inserted by the editor (videoBlockHtml) — captured so it can be removed whole.
const BLOCK_RE = /\n?<div style="margin:18px 0;border:1px solid #e5e7eb;border-radius:16px;overflow:hidden;background:#000;">\s*<video[^>]*\ssrc="([^"]+)"[\s\S]*?<\/video>[\s\S]*?<\/div>\s*<\/div>\n?/gi;

export function removeVideoBlock(html, src) {
  return html.replace(BLOCK_RE, (m, s) => (s === src ? "\n" : m));
}

export default function PostVideosList({ userRecordId, gridIds, onRemoveGrid, html, onRemoveHtml }) {
  const [lib, setLib] = useState([]);
  useEffect(() => { fetchPostVideos(userRecordId).then((r) => setLib(r.items || [])).catch(() => {}); }, [userRecordId]);
  const byId = Object.fromEntries(lib.map((v) => [v.post_rec_id, v]));
  const byUrl = Object.fromEntries(lib.map((v) => [v.video_url, v]));
  const blocks = Array.from(html.matchAll(BLOCK_RE), (m) => m[1]);
  const items = [
    ...gridIds.map((id) => ({ key: `g-${id}`, kind: "grid", id, v: byId[id] })),
    ...blocks.map((src, i) => ({ key: `h-${i}-${src}`, kind: "html", src, v: byUrl[src] })),
  ];
  if (!items.length) return null;
  const urlCount = items.reduce((m, it) => { const u = it.v?.video_url || it.src; if (u) m[u] = (m[u] || 0) + 1; return m; }, {});
  return (
    <div className="rounded-xl border border-zinc-200 p-3 space-y-2" data-testid="post-videos-list">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-zinc-700">Videos in this post · {items.length}</div>
        <div className="text-[11px] text-zinc-500">Click × to remove. Grid changes apply when you save.</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
        {items.map((it) => {
          const u = it.v?.video_url || it.src;
          const dup = u && urlCount[u] > 1;
          return (
            <div key={it.key} className={`flex items-center gap-2 rounded-lg border p-1.5 ${dup ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`} data-testid={`post-video-item-${it.kind}`}>
              <div className="h-12 w-8 shrink-0 rounded bg-zinc-900 overflow-hidden flex items-center justify-center">
                {it.v?.thumbnail_url ? <img src={it.v.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <Clapperboard size={12} className="text-zinc-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold text-zinc-800 truncate">{it.v?.business_name || (it.kind === "html" ? "Inserted video" : "Video")}</div>
                <div className="text-[10px] text-zinc-500 truncate">{it.v?.template_name || (it.kind === "grid" ? "In grid" : "")}{dup ? " · duplicate" : ""}</div>
              </div>
              <button type="button" title="Remove from post" onClick={() => (it.kind === "grid" ? onRemoveGrid(it.id) : onRemoveHtml(it.src))}
                className="h-6 w-6 shrink-0 rounded-full hover:bg-rose-100 text-zinc-500 hover:text-rose-700 flex items-center justify-center" data-testid={`post-video-remove-${it.kind}`}>
                <X size={12} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
